import { assign, fromPromise, setup } from 'xstate';
import type { FeeAuthorization, FeeSchedule } from '~/features/employer/billing/types';
import { API_CALLS as b2bApi } from '~/features/market/job-opportunities/api';
import type {
  B2BListingResponse,
  B2BResponsesSide,
} from '~/features/market/job-opportunities/types';
import { API_CALLS as jobApi } from '~/features/market/job-responses/provider-responses/api';
import type {
  ProviderJobMessage,
  ProviderJobResponse,
} from '~/features/market/job-responses/provider-responses/types';
import {
  acceptConflictCode,
  feeRefusalCode,
  feeRefusalSchedule,
  isPayerFeeAuthorizationRequired,
  isUncertainListingActionError,
  listingActionRefusalMessage,
  PAYER_FEE_AUTHORIZATION_REQUIRED_MESSAGE,
  replyRefusalFeedback,
  type FeeRefusalCode,
} from '~/features/market/listing-search/errors';
import { MARKET_ACTION_TIMEOUT_MS, MARKET_READ_TIMEOUT_MS } from '~/features/market/requestPolicy';
import { withRequestDeadline } from '~/utils/requestDeadline';

export type ReceivedResponseScope = {
  workspace: 'job' | 'b2b';
  responseId: number;
  listingId: number;
  expectedNegotiationId?: number;
  /** [MCV-1889] The B2B Buying | Selling tab the row was read under; the recovery read needs it. */
  side?: B2BResponsesSide;
};
export type ReceivedResponseAction = ReceivedResponseScope & {
  kind: 'reply' | 'accept' | 'accept_listing' | 'withdraw' | 'reject';
  messageBody?: string;
  /** [MCV-1858] The B2B payer's acknowledged platform-fee schedule; accept only. */
  feeAuthorization?: FeeAuthorization;
};
export const receivedResponseKey = (scope: ReceivedResponseScope) =>
  `${scope.workspace}:${scope.listingId}:${scope.responseId}`;

type RefreshedResponse = (
  | { workspace: 'job'; response: ProviderJobResponse | null }
  | { workspace: 'b2b'; response: B2BListingResponse | null }
) & { messages: ProviderJobMessage[]; action: ReceivedResponseAction };

// [MCV-1850] The recovery read must find the row whatever its status: after a
// decline or withdrawal the default list no longer returns it.
const readOriginal = async (
  action: ReceivedResponseAction,
  signal: AbortSignal
): Promise<RefreshedResponse> => {
  if (action.workspace === 'job') {
    const rows = await jobApi.getListingResponses({
      filter: 'all',
      listingId: action.listingId,
      includeDeclined: true,
      signal,
    });
    signal.throwIfAborted();
    const response = rows.find(
      (row) => row.responseId === action.responseId && row.listing.id === action.listingId
    );
    const messages = await jobApi.getMessages(action.responseId, signal);
    return { workspace: 'job', response: response ?? null, messages, action };
  }
  const rows = await b2bApi.getListingResponses({
    filter: 'all',
    listingId: action.listingId,
    includeDeclined: true,
    side: action.side,
    signal,
  });
  signal.throwIfAborted();
  const response = rows.find(
    (row) => row.responseId === action.responseId && row.listing.id === action.listingId
  );
  const messages = await b2bApi.getMessages(action.responseId, signal);
  return { workspace: 'b2b', response: response ?? null, messages, action };
};

export const receivedResponseActionsMachine = setup({
  types: {
    context: {} as {
      drafts: Record<string, string>;
      errors: Record<string, string>;
      /** Which action each entry in `errors` refused, so replies can surface in the composer. */
      errorKinds: Record<string, ReceivedResponseAction['kind']>;
      /**
       * [MCV-1858] Set when an accept was refused for its platform-fee schedule, so the view can
       * show the (refetched) schedule again instead of only an alert.
       */
      feeRefusals: Record<string, FeeRefusalCode>;
      /** [MCV-1933] The schedule the server sent with a refusal, when it sent one. */
      feeRefusalSchedules: Record<string, FeeSchedule>;
      pending: ReceivedResponseAction | null;
      uncertain: ReceivedResponseAction | null;
      receipt: {
        action: ReceivedResponseAction;
        message: ProviderJobMessage | undefined;
        revision: number;
      } | null;
      refreshed: (RefreshedResponse & { revision: number }) | null;
      revision: number;
      readError: string | null;
    },
    events: {} as
      | { type: 'EDIT'; scope: ReceivedResponseScope; body: string }
      | { type: 'WRITE'; action: ReceivedResponseAction }
      | { type: 'REVIEW' }
      | { type: 'CONSUME_FEE_REFUSAL'; scope: ReceivedResponseScope }
      | { type: 'ACKNOWLEDGE'; scope: ReceivedResponseScope; revision: number }
      | { type: 'DISCARD' },
  },
  actors: {
    write: fromPromise(
      async ({ input, signal }: { input: ReceivedResponseAction; signal: AbortSignal }) =>
        withRequestDeadline(
          async (requestSignal) => {
            if (input.kind === 'reply') {
              const api = input.workspace === 'job' ? jobApi : b2bApi;
              return api.sendMessage(input.responseId, input.messageBody ?? '', requestSignal);
            }
            if (input.workspace === 'b2b') {
              // A b2b decline is a status change, never an acceptance: routing it to
              // acceptOpportunity would accept the responder's offer.
              if (input.kind === 'reject') {
                await b2bApi.rejectOpportunity(input.responseId, requestSignal);
              } else if (input.feeAuthorization) {
                await b2bApi.acceptOpportunity(
                  input.responseId,
                  input.expectedNegotiationId,
                  requestSignal,
                  input.feeAuthorization
                );
              } else {
                await b2bApi.acceptOpportunity(
                  input.responseId,
                  input.expectedNegotiationId,
                  requestSignal
                );
              }
            } else if (input.kind === 'accept') {
              // [MCV-1933] A JOB accept carries the payer's fee authorization too.
              await jobApi.acceptResponse(
                input.responseId,
                input.expectedNegotiationId,
                requestSignal,
                ...(input.feeAuthorization ? ([input.feeAuthorization] as const) : [])
              );
            } else if (input.kind === 'accept_listing') {
              await jobApi.acceptListing(
                input.responseId,
                requestSignal,
                ...(input.feeAuthorization ? ([input.feeAuthorization] as const) : [])
              );
            } else if (input.kind === 'withdraw') {
              await jobApi.withdrawResponse(input.responseId, requestSignal);
            } else if (input.kind === 'reject') {
              await jobApi.rejectResponse(input.responseId, requestSignal);
            } else {
              // A new job kind must get its own branch: falling through to
              // reject would silently reject the applicant.
              const unreachable: never = input.kind;
              throw new Error(`Unsupported received response action: ${String(unreachable)}`);
            }
            return undefined;
          },
          MARKET_ACTION_TIMEOUT_MS,
          signal
        )
    ),
    refresh: fromPromise(
      ({ input, signal }: { input: ReceivedResponseAction; signal: AbortSignal }) =>
        withRequestDeadline(
          (requestSignal) => readOriginal(input, requestSignal),
          MARKET_READ_TIMEOUT_MS,
          signal
        )
    ),
  },
  actions: {
    clearTracking: assign({
      uncertain: null,
      pending: null,
      refreshed: null,
      readError: null,
    }),
    discardDraftAndTracking: assign(({ context }) => {
      const drafts = { ...context.drafts };
      if (context.uncertain) delete drafts[receivedResponseKey(context.uncertain)];
      return { uncertain: null, pending: null, refreshed: null, readError: null, drafts };
    }),
  },
}).createMachine({
  id: 'receivedResponseActions',
  context: {
    drafts: {},
    errors: {},
    errorKinds: {},
    feeRefusals: {},
    feeRefusalSchedules: {},
    pending: null,
    uncertain: null,
    receipt: null,
    refreshed: null,
    revision: 0,
    readError: null,
  },
  initial: 'ready',
  states: {
    ready: {
      on: {
        EDIT: {
          guard: ({ context }) => !context.uncertain,
          actions: assign(({ context, event }) => {
            const drafts = { ...context.drafts };
            if (event.body) drafts[receivedResponseKey(event.scope)] = event.body;
            else delete drafts[receivedResponseKey(event.scope)];
            return { drafts };
          }),
        },
        WRITE: {
          guard: ({ context, event }) =>
            !context.uncertain &&
            !context.readError &&
            (event.action.workspace === 'job' ||
              event.action.kind === 'reply' ||
              event.action.kind === 'accept' ||
              event.action.kind === 'reject') &&
            (event.action.kind !== 'reply' || Boolean(event.action.messageBody?.trim())),
          target: 'writing',
          actions: assign(({ context, event }) => {
            const pending = { ...event.action, messageBody: event.action.messageBody?.trim() };
            const key = receivedResponseKey(pending);
            const errors = { ...context.errors };
            delete errors[key];
            const errorKinds = { ...context.errorKinds };
            delete errorKinds[key];
            const feeRefusals = { ...context.feeRefusals };
            delete feeRefusals[key];
            const feeRefusalSchedules = { ...context.feeRefusalSchedules };
            delete feeRefusalSchedules[key];
            return {
              pending,
              errors,
              errorKinds,
              feeRefusals,
              feeRefusalSchedules,
              refreshed: null,
              readError: null,
              drafts:
                pending.kind === 'reply'
                  ? { ...context.drafts, [key]: pending.messageBody ?? '' }
                  : context.drafts,
            };
          }),
        },
        // [MCV-1858] The view has reacted to a fee-schedule refusal (it reopened the dialog), so
        // neither the refusal nor its alert text may fire again on a later visit to this row.
        CONSUME_FEE_REFUSAL: {
          guard: ({ context, event }) => receivedResponseKey(event.scope) in context.feeRefusals,
          actions: assign(({ context, event }) => {
            const key = receivedResponseKey(event.scope);
            const { [key]: _refusal, ...feeRefusals } = context.feeRefusals;
            const { [key]: _schedule, ...feeRefusalSchedules } = context.feeRefusalSchedules;
            const { [key]: _error, ...errors } = context.errors;
            const { [key]: _kind, ...errorKinds } = context.errorKinds;
            return { feeRefusals, feeRefusalSchedules, errors, errorKinds };
          }),
        },
        REVIEW: {
          guard: ({ context }) => context.uncertain !== null || context.pending !== null,
          target: 'refreshing',
          actions: assign({
            pending: ({ context }) => context.uncertain ?? context.pending,
            refreshed: null,
            readError: null,
          }),
        },
        ACKNOWLEDGE: {
          guard: ({ context, event }) =>
            context.uncertain !== null &&
            receivedResponseKey(event.scope) === receivedResponseKey(context.uncertain) &&
            context.refreshed?.revision === event.revision &&
            receivedResponseKey(context.refreshed.action) ===
              receivedResponseKey(context.uncertain),
          actions: 'clearTracking',
        },
        DISCARD: { actions: 'discardDraftAndTracking' },
      },
    },
    writing: {
      invoke: {
        src: 'write',
        input: ({ context }) => {
          if (!context.pending) throw new Error('No received response operation selected.');
          return context.pending;
        },
        onDone: {
          target: 'refreshing',
          actions: assign(({ context, event }) => {
            if (!context.pending) return {};
            const drafts = { ...context.drafts };
            if (context.pending.kind === 'reply')
              delete drafts[receivedResponseKey(context.pending)];
            return {
              drafts,
              revision: context.revision + 1,
              receipt: {
                action: context.pending,
                message: event.output,
                revision: context.revision + 1,
              },
            };
          }),
        },
        onError: [
          {
            guard: ({ event }) => isUncertainListingActionError(event.error),
            target: 'refreshing',
            actions: assign({ uncertain: ({ context }) => context.pending }),
          },
          {
            // [MCV-1858] The listing owner (payer) has not authorized the current fee schedule, so
            // this responder cannot accept yet. The refusal is definite, not uncertain, and is not
            // a fee-dialog refusal (the responder is not the payer). The server cleared the
            // payer's acceptance, so re-read the response and keep the explanation visible.
            // [MCV-1889] A lost accept race (listing filled, or a concurrent write) is the same
            // kind of definite refusal over a row that is now stale.
            guard: ({ event }) =>
              isPayerFeeAuthorizationRequired(event.error) ||
              acceptConflictCode(event.error) !== null,
            target: 'refreshing',
            actions: assign(({ context, event }) => ({
              errors: context.pending
                ? {
                    ...context.errors,
                    [receivedResponseKey(context.pending)]:
                      listingActionRefusalMessage(event.error) ??
                      PAYER_FEE_AUTHORIZATION_REQUIRED_MESSAGE,
                  }
                : context.errors,
              errorKinds: context.pending
                ? {
                    ...context.errorKinds,
                    [receivedResponseKey(context.pending)]: context.pending.kind,
                  }
                : context.errorKinds,
            })),
          },
          {
            target: 'ready',
            actions: assign(({ context, event }) => ({
              pending: null,
              errors: context.pending
                ? {
                    ...context.errors,
                    [receivedResponseKey(context.pending)]:
                      context.pending.kind === 'reply'
                        ? replyRefusalFeedback(
                            event.error,
                            'Your message was not sent. Review the text and try again.'
                          )
                        : (listingActionRefusalMessage(event.error) ??
                          'The status change was refused. Refresh before trying again.'),
                  }
                : context.errors,
              errorKinds: context.pending
                ? {
                    ...context.errorKinds,
                    [receivedResponseKey(context.pending)]: context.pending.kind,
                  }
                : context.errorKinds,
              feeRefusals:
                context.pending && feeRefusalCode(event.error)
                  ? {
                      ...context.feeRefusals,
                      [receivedResponseKey(context.pending)]: feeRefusalCode(
                        event.error
                      ) as FeeRefusalCode,
                    }
                  : context.feeRefusals,
              feeRefusalSchedules:
                context.pending && feeRefusalSchedule(event.error)
                  ? {
                      ...context.feeRefusalSchedules,
                      [receivedResponseKey(context.pending)]: feeRefusalSchedule(
                        event.error
                      ) as FeeSchedule,
                    }
                  : context.feeRefusalSchedules,
            })),
          },
        ],
      },
    },
    refreshing: {
      invoke: {
        src: 'refresh',
        input: ({ context }) => {
          if (!context.pending) throw new Error('No received response operation to refresh.');
          return context.pending;
        },
        onDone: {
          target: 'ready',
          actions: assign({
            pending: null,
            revision: ({ context }) => context.revision + 1,
            refreshed: ({ context, event }) => ({
              ...event.output,
              revision: context.revision + 1,
            }),
            readError: null,
          }),
        },
        onError: {
          target: 'ready',
          actions: assign({
            readError: 'Unable to refresh the original response. Refresh before acting again.',
          }),
        },
      },
    },
  },
});
